// Hover integration — client helpers (Oct 1, 2026).
// Server brain: supabase/functions/hover-sync. Jobs are created automatically
// when a claim is filed (trg_hover_on_claim_filed) and land in the deal
// owner's Hover app; measurements come back by webhook + a 20-min poll.

import { supabase } from "./supabase";
import { invokeErrorMessage } from "./edgeError";

// Public: the OAuth client id is visible in every Hover redirect anyway.
export const HOVER_CLIENT_ID = "7510f1ccf22c2f59e6fcffb9311f1ab2e4253a52b63a42c11db65a07a94aa6e1";
export const HOVER_REDIRECT_URI = "https://lxymewxomybjdrmgchqt.supabase.co/functions/v1/hover-oauth-callback";

export type HoverState = "requested" | "capturing" | "processing" | "complete" | "failed" | "error";

export interface HoverMeasurements {
  eaves: number | null; rakes: number | null; ridges: number | null; hips: number | null;
  valleys: number | null; flashing: number | null; step_flashing: number | null;
  roof_area_sqft: number | null; squares: number | null; sq_with_waste: number | null;
  waste_pct: number; predominant_pitch: string | null; ridges_hips_combined?: boolean;
}

export interface HoverJob {
  id: string;
  deal_id: string;
  hover_job_id: number | null;
  hover_model_id: number | null;
  assigned_email: string | null;
  state: HoverState;
  hover_state_raw: string | null;
  measurements: HoverMeasurements | null;
  pdf_storage_path: string | null;
  measurements_applied_at: string | null;
  error: string | null;
  trigger_source: string;
  created_at: string;
  completed_at: string | null;
  last_checked_at: string | null;
}

export const HOVER_STATE_META: Record<HoverState, { label: string; cls: string }> = {
  requested:  { label: "Requested",                 cls: "bg-slate-100 text-slate-700" },
  capturing:  { label: "Waiting on photos",         cls: "bg-sky-100 text-sky-700" },
  processing: { label: "Hover is measuring",        cls: "bg-amber-100 text-amber-800" },
  complete:   { label: "Measurements ready",        cls: "bg-emerald-100 text-emerald-700" },
  failed:     { label: "Hover couldn't finish",     cls: "bg-rose-100 text-rose-700" },
  error:      { label: "Couldn't create job",       cls: "bg-rose-100 text-rose-700" },
};

export async function hoverSync<T = any>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("hover-sync", { body });
  if (error) throw new Error(await invokeErrorMessage(error, "Hover request failed"));
  return data as T;
}

export async function fetchHoverJobs(dealId: string): Promise<HoverJob[]> {
  const { data, error } = await supabase.from("hover_jobs").select("*")
    .eq("deal_id", dealId).order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as HoverJob[];
}

export async function startHoverConnect(): Promise<void> {
  const { data: state, error } = await supabase.rpc("hover_oauth_create_state");
  if (error || !state) throw new Error(error?.message ?? "Couldn't start the Hover connection");
  window.location.href =
    `https://hover.to/oauth/authorize?response_type=code` +
    `&client_id=${encodeURIComponent(HOVER_CLIENT_ID)}` +
    `&redirect_uri=${encodeURIComponent(HOVER_REDIRECT_URI)}` +
    `&state=${encodeURIComponent(state as string)}`;
}
